import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import LayoutCreditRefresh from './LayoutCreditRefresh';
import { carouselConfigShape } from './AmexTabbedCarouselRefresh.props';
import {
  TABBED_CAROUSEL_REFRESH_DEFAULT_ACTIVE_INDEX,
  TABBED_CAROUSEL_REFRESH_LAYOUTS,
  TABBED_CAROUSEL_REFRESH_MAX_TABS,
} from './AmexTabbedCarouselRefresh.constants';
import './AmexTabbedCarouselRefresh.scss';
import {
  convertToPlainText,
  getScrollBehaviorFromMotionPreference,
  handleMktAnalytics,
} from '../AmexUtils';
import { CTA_EVENT_NAME } from '../constants';

const AmexTabbedCarouselRefresh = ({
  awsFolderName,
  baseRequestUrl,
  carouselConfig,
  hash,
  matchMedia,
  Analytics,
}) => {
  const {
    id,
    initial_active_index: initialActiveIndex,
    section_intro: sectionIntro,
    section_title: sectionTitle,
    sections,
  } = carouselConfig || {};

  const initialIndex = initialActiveIndex
    ? initialActiveIndex - 1
    : TABBED_CAROUSEL_REFRESH_DEFAULT_ACTIVE_INDEX;
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [expandedMobileIndex, setExpandedMobileIndex] = useState(null);
  const mobileTabRefs = useRef([]);
  const pendingMobileScrollIndex = useRef(null);
  const mobileScrollTimeoutRef = useRef(null);
  const desktopTabRefs = useRef([]);
  const isDesktop = !!matchMedia?.lg;

  const updatedSections = useMemo(() => (Array.isArray(sections) ? sections : [])
    .filter((section) => {
      const requiredFields = [
        section.id,
        section.title,
        section.layout,
        section.content,
      ];
      return (
        !section.hide_section
        && requiredFields.every((field) => field)
        && Object.keys(section.content).length > 0
      );
    })
    .sort((a, b) => {
      const aHasOrder = Number.isFinite(a?.order_id);
      const bHasOrder = Number.isFinite(b?.order_id);
      if (aHasOrder && bHasOrder) {
        return a.order_id - b.order_id; // handles equal values -> 0
      }
      if (aHasOrder && !bHasOrder) return -1; // ordered items first
      if (!aHasOrder && bHasOrder) return 1;
      return 0; // both missing order_id: keep original relative order
    })
    .slice(0, TABBED_CAROUSEL_REFRESH_MAX_TABS), [sections]);

  useEffect(() => {
    if (!updatedSections.length) return;
    if (activeIndex > updatedSections.length - 1) setActiveIndex(0);
    if (expandedMobileIndex > updatedSections.length - 1) setExpandedMobileIndex(null);
  }, [activeIndex, expandedMobileIndex, updatedSections]);

  useEffect(() => {
    const tabIds = updatedSections.map((section) => section.id);
    if (!hash || !tabIds.length) return;
    const currentHash = hash.replace('#', '');
    if (currentHash && tabIds.includes(currentHash)) {
      const hashMatchedIndex = tabIds.indexOf(currentHash);
      setActiveIndex(hashMatchedIndex);
      setExpandedMobileIndex(hashMatchedIndex);
    }
  }, [hash, updatedSections]);

  const handleKeyDown = (event) => {
    const count = updatedSections.length;
    let nextIndex = activeIndex;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (activeIndex + 1) % count;
        break;
      case 'ArrowLeft':
        nextIndex = (activeIndex - 1 + count) % count;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = count - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    setActiveIndex(nextIndex);
    window.requestAnimationFrame(() => {
      desktopTabRefs.current[nextIndex]?.focus({ preventScroll: true });
    });
  };

  const handleClickWithTagging = (item) => {
    const specificParamsAmplitude = {
      tab_item: convertToPlainText(item.title),
      action_name: 'Tab Click',
    };
    handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, specificParamsAmplitude);
  };

  const handleClick = (index, item) => {
    setActiveIndex(index);
    handleClickWithTagging(item);
  };

  const scrollMobileTabToTop = (index) => {
    const tabElement = mobileTabRefs.current[index];
    if (!tabElement) return;

    const targetTop = tabElement.getBoundingClientRect().top + window.scrollY - 50;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: getScrollBehaviorFromMotionPreference(),
    });
  };

  useEffect(() => {
    if (expandedMobileIndex === null) return undefined;
    if (pendingMobileScrollIndex.current !== expandedMobileIndex) return undefined;

    if (mobileScrollTimeoutRef.current) clearTimeout(mobileScrollTimeoutRef.current);
    mobileScrollTimeoutRef.current = setTimeout(() => {
      scrollMobileTabToTop(expandedMobileIndex);
      pendingMobileScrollIndex.current = null;
      mobileScrollTimeoutRef.current = null;
    }, 550);

    return () => {
      if (mobileScrollTimeoutRef.current) {
        clearTimeout(mobileScrollTimeoutRef.current);
        mobileScrollTimeoutRef.current = null;
      }
    };
  }, [expandedMobileIndex]);

  if (!carouselConfig || !updatedSections.length) return null;

  const renderLayout = (section) => {
    if (section.layout !== TABBED_CAROUSEL_REFRESH_LAYOUTS.TEMPLATE_F) return null;
    return (
      <LayoutCreditRefresh
        awsFolderName={awsFolderName}
        baseRequestUrl={baseRequestUrl}
        isStandalone={false}
        content={section.content}
        tabHeading={section.title}
        tabThumbImage={section.thumb_image}
        matchMedia={matchMedia}
        Analytics={Analytics}
      />
    );
  };

  const renderContent = (view) => updatedSections.map((item, index) => {
    const {
      thumb_image_tab: tabThumbImageIcon,
      title,
      id: sectionId,
    } = item;
    const isDesktopActive = activeIndex === index;
    const isMobileActive = expandedMobileIndex === index;
    const thumbImageTab = `${baseRequestUrl}${awsFolderName}/images/${tabThumbImageIcon}`;

    switch (view) {
      case 'mobile':
        return (
          <div className="AmexTabbedCarouselRefresh__accordion--item" key={`accordion-item-${sectionId}`}>
            <h3>
              <Button
                variant="no-style"
                type="button"
                id={sectionId}
                ref={(tabButton) => {
                  mobileTabRefs.current[index] = tabButton;
                }}
                aria-expanded={isMobileActive}
                className={`AmexTabbedCarouselRefresh__mobile-tab ${isMobileActive ? 'active' : ''}`}
                onClick={() => setExpandedMobileIndex((currentExpandedMobileIndex) => {
                  const nextExpandedMobileIndex = currentExpandedMobileIndex === index ? null : index;
                  pendingMobileScrollIndex.current = nextExpandedMobileIndex;
                  return nextExpandedMobileIndex;
                })}
              >
                <span className="AmexTabbedCarouselRefresh__mobile-tab-content">
                  <img
                    className="AmexTabbedCarouselRefresh__mobile-tab-thumb"
                    src={thumbImageTab}
                    alt=""
                  />
                  <span
                    className="AmexTabbedCarouselRefresh__mobile-tab-title"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: sanitizeString(title) }}
                  />
                </span>

              </Button>
            </h3>
            <div
              role="region"
              aria-labelledby={sectionId}
              id={`tabbedCarouselMobileDesktopPanel${index}`}
              aria-hidden={!isMobileActive}
              className={cx('AmexTabbedCarouselRefresh__accordion--content', {
                active: isMobileActive,
              })}
            >
              <div
                className={cx('AmexTabbedCarouselRefresh__accordion--inner', {
                  active: isMobileActive,
                })}
              >
                {renderLayout(item)}
              </div>
            </div>
          </div>
        );
      case 'desktopPanel':
        return (
          <div
            key={`desktop-panel-${sectionId}`}
            role="tabpanel"
            id={`tabbedCarouselDesktopPanel${index}`}
            hidden={!isDesktopActive}
            aria-labelledby={sectionId}
          >
            {renderLayout(item)}
          </div>
        );
      case 'desktopTabs':
      default:
        return (
          <Button
            key={sectionId}
            variant="no-style"
            type="button"
            role="tab"
            id={sectionId}
            ref={(el) => {
              desktopTabRefs.current[index] = el;
            }}
            aria-controls={`tabbedCarouselDesktopPanel${index}`}
            aria-selected={isDesktopActive}
            className={`AmexTabbedCarouselRefresh__tab ${isDesktopActive ? 'active' : ''}`}
            onClick={() => handleClick(index, item)}
            onKeyDown={handleKeyDown}
            tabIndex={isDesktopActive ? 0 : -1}
          >
            <span className="AmexTabbedCarouselRefresh__tab-content">
              <img
                className="AmexTabbedCarouselRefresh__tab-thumb"
                src={thumbImageTab}
                alt=""
              />
              <span
                className="AmexTabbedCarouselRefresh__tab-title"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitizeString(title) }}
              />
            </span>
          </Button>
        );
    }
  });

  const renderMobile = () => (
    <div className="AmexTabbedCarouselRefresh__accordion">
      {renderContent('mobile')}
    </div>
  );

  const renderDesktop = () => (
    <>
      <div
        role="tablist"
        className="AmexTabbedCarouselRefresh__tabs"
        aria-labelledby={id}
      >
        {renderContent('desktopTabs')}
      </div>
      <div className="AmexTabbedCarouselRefresh__panels">
        {renderContent('desktopPanel')}
      </div>
    </>
  );

  return (
    <section className="AmexTabbedCarouselRefresh">
      <div className="container">
        {sectionTitle && (
          <h2
            id={id}
            className="AmexTabbedCarouselRefresh__title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizeString(sectionTitle) }}
          />
        )}
        {sectionIntro && (
          <p
            className="AmexTabbedCarouselRefresh__intro"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizeString(sectionIntro) }}
          />
        )}
        {isDesktop ? renderDesktop() : renderMobile()}
      </div>
    </section>
  );
};

AmexTabbedCarouselRefresh.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  carouselConfig: carouselConfigShape,
  hash: PropTypes.string,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }),
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }),
};

AmexTabbedCarouselRefresh.defaultProps = {
  carouselConfig: null,
  hash: null,
  matchMedia: {},
  Analytics: {},
};

export default AmexTabbedCarouselRefresh;
